import { type AggregationFnDef, constructAggregationFn } from '@tanstack/react-table';
import { ValueType } from '../types/ValueType';
import type { DataTableAggregate } from './DataTable.types';
import { formatCellText } from './dataTableFormat';

const isFilled = (raw: unknown): boolean => raw != null && raw !== '';

/** Числа из значений ячеек. Пустая ячейка — не ноль: `Number(null) === 0`
 *  занижал бы минимум и среднее. Число строкой («1234.5» в данных форм) — число. */
const numbersOf = (values: readonly unknown[]): number[] => {
    const numbers: number[] = [];
    for (const raw of values) {
        if (!isFilled(raw)) continue;
        const value = Number(raw);
        if (Number.isFinite(value)) numbers.push(value);
    }
    return numbers;
};

const sum = (values: readonly number[]): number => values.reduce((acc, value) => acc + value, 0);
const pick = (values: readonly number[], better: (a: number, b: number) => boolean): number | undefined =>
    values.length ? values.reduce((acc, value) => (better(value, acc) ? value : acc)) : undefined;
const isLess = (a: number, b: number) => a < b;
const isGreater = (a: number, b: number) => a > b;

/**
 * Агрегат над значениями ячеек — общий для строки группы (`aggregate` колонки) и
 * итогов полноэкранного режима (`total`). `count` — заполненные ячейки: у текстовой
 * колонки число значений тоже осмысленно.
 */
export function aggregateValues(aggregate: DataTableAggregate, values: readonly unknown[]): number | undefined {
    switch (aggregate) {
        case 'sum':
            return sum(numbersOf(values));
        case 'count':
            return values.filter(isFilled).length;
        case 'min':
            return pick(numbersOf(values), isLess);
        case 'max':
            return pick(numbersOf(values), isGreater);
        case 'mean': {
            const numbers = numbersOf(values);
            return numbers.length ? sum(numbers) / numbers.length : undefined;
        }
    }
}

// определения общие для любых строк, как и встроенные в библиотеку
const overRows =
    (aggregate: DataTableAggregate): AggregationFnDef['aggregate'] =>
    ({ rows, getValue }) =>
        aggregateValues(
            aggregate,
            rows.map((row) => getValue(row)),
        );
const subResults = ({ subRowResults }: { subRowResults: readonly unknown[] }): number[] =>
    subRowResults.filter((value): value is number => typeof value === 'number');

/**
 * Реестр агрегатов таблицы. `merge` собирает вложенную группировку из результатов
 * подгрупп без повторного прохода по строкам; у среднего его нет — среднее средних
 * не равно среднему, и библиотека считает по строкам группы.
 */
export const dataTableAggregationFns = {
    sum: constructAggregationFn({ aggregate: overRows('sum'), merge: (ctx) => sum(subResults(ctx)) }),
    count: constructAggregationFn({ aggregate: overRows('count'), merge: (ctx) => sum(subResults(ctx)) }),
    min: constructAggregationFn({ aggregate: overRows('min'), merge: (ctx) => pick(subResults(ctx), isLess) }),
    max: constructAggregationFn({ aggregate: overRows('max'), merge: (ctx) => pick(subResults(ctx), isGreater) }),
    mean: constructAggregationFn({ aggregate: overRows('mean') }),
} satisfies Record<DataTableAggregate, unknown>;

export const DATA_TABLE_AGGREGATE_LABEL: Record<DataTableAggregate, string> = {
    sum: 'сумма',
    count: 'кол-во',
    min: 'мин.',
    max: 'макс.',
    mean: 'среднее',
};

const NUMERIC_TYPES = new Set<ValueType | undefined>([ValueType.Money, ValueType.Double, ValueType.Integer]);

/** Текст агрегата: количество — целым, остальное — в числовом типе колонки
 *  (у нечисловой — дробным: сумма по «Да/Нет» не булево значение). */
export const formatAggregate = (value: unknown, aggregate: DataTableAggregate, dataType?: ValueType): string =>
    formatCellText(
        value,
        aggregate === 'count' ? ValueType.Integer : NUMERIC_TYPES.has(dataType) ? dataType : ValueType.Double,
    );
